import { useEffect, type ReactElement } from 'react';
import { TaskKind, type TaskDto } from '@gr/core/platform/tasks/dto';
import type { RecompressParamsDto, RecompressReportDto, StorageRecountReportDto } from '@gr/core/content/media/recompress';
import { Link, useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { useObservable } from '../../../shared/store';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { RecompressTaskDetails, StorageRecountDetails } from '../../content/media';
import { BLOG_TASKS_SECTION, taskKindKey } from './model/task-view';
import { TaskCard } from './TaskCard/TaskCard';
import { TaskList } from './TaskList/TaskList';
import { useTaskActions } from './use-task-actions';

/** Параметры и итог вида задачи (TASK-UC-3): что именно — решает контекст вида. */
function TaskKindDetails({ task }: { task: TaskDto }): ReactElement | null {
  switch (task.kind) {
    case TaskKind.MediaRecompress:
      return task.blogId ? <RecompressTaskDetails blogId={task.blogId} params={task.params as RecompressParamsDto} report={task.report as RecompressReportDto | null} /> : null;
    case TaskKind.MediaStorageRecount:
      return <StorageRecountDetails report={task.report as StorageRecountReportDto | null} />;
    // Задачи платформы в блоге не бывает: их отчёт — в «Резервных копиях» администратора.
    case TaskKind.Backup:
    case TaskKind.Restore:
      return null;
  }
}

/** Задачи блога из API и соединения событий — для раздела настроек и отчёта. */
function useBlogTasks(blogId: string, taskId?: string) {
  const { api, tasks } = useServices();
  const all = useObservable(tasks.tasks);
  const loaded = useResource(() => (taskId ? api.tasks.get(blogId, taskId).then((r) => ({ ...r, data: r.success ? [r.data] : [] })) : api.tasks.list(blogId)), [api, blogId, taskId]);
  useEffect(() => {
    if (loaded.data) tasks.merge(loaded.data);
  }, [loaded.data, tasks]);
  const shown = [...all.values()].filter((task) => task.blogId === blogId && (!taskId || task.id === taskId)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return { loaded, shown };
}

// TASK-UC-3, TASK-RULE-3: задачи блога у всех участников — раздел «Фоновые задачи» настроек блога.
export function BlogTasksSection(): ReactElement {
  const { t, te } = useT();
  const { blog } = useBlog();
  const actions = useTaskActions();
  const { loaded, shown } = useBlogTasks(blog.id);
  const error = loaded.error ?? actions.error;
  return (
    <div className="u-stack">
      <p className="u-muted u-small">{t('tasks.page.subtitle')}</p>
      {error ? <Alert tone="danger">{te(error)}</Alert> : null}
      {loaded.loading && shown.length === 0 ? (
        <Loading label={t('common.loading')} />
      ) : (
        <TaskList
          tasks={shown}
          empty={<EmptyState title={t('tasks.page.empty')} text={t('tasks.page.emptyText')} />}
          timezone={blog.timezone}
          withReportLinks
          canManage={actions.canManage}
          pendingId={actions.pendingId}
          onCancel={(task) => void actions.cancel(task)}
          onResume={(task) => void actions.resume(task)}
        />
      )}
    </div>
  );
}

// TASK-UC-3: отчёт задачи блога; изменения — по соединению событий.
export function BlogTaskPage({ taskId }: { taskId: string }): ReactElement {
  const { t, te } = useT();
  const { blog } = useBlog();
  const actions = useTaskActions();
  const { loaded, shown } = useBlogTasks(blog.id, taskId);
  const error = loaded.error ?? actions.error;
  const task = shown[0];
  return (
    <div className="u-stack-lg">
      <Link to="settings" params={{ blogId: blog.id, section: BLOG_TASKS_SECTION }}>
        {t('tasks.page.back')}
      </Link>
      {task ? (
        <>
          <PageHeader title={t(`tasks.kinds.${taskKindKey(task.kind)}`)} />
          {error ? <Alert tone="danger">{te(error)}</Alert> : null}
          <Card>
            <TaskCard
              task={task}
              timezone={blog.timezone}
              canManage={actions.canManage(task)}
              pending={actions.pendingId === task.id}
              onCancel={(item) => void actions.cancel(item)}
              onResume={(item) => void actions.resume(item)}
            >
              <TaskKindDetails task={task} />
            </TaskCard>
          </Card>
        </>
      ) : loaded.loading ? (
        <Loading label={t('common.loading')} />
      ) : (
        <Alert tone="danger">{te(loaded.error ?? [])}</Alert>
      )}
    </div>
  );
}
